import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, Minus, MessageSquare,
} from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import type { Driver01Data, ComplaintsArea, DriverKpi } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { formatHighlights } from '../../../utils/Format';

/* ─── Helpers ────────────────────────────────────────────── */
type TrendDir = 'better' | 'worse' | 'equal';

function getTrend(current: number, previous: number): TrendDir {
  if (current === previous) return 'equal';
  return current < previous ? 'better' : 'worse';
}

const TrendBadge = ({ trend, priorPeriod }: { trend: TrendDir; priorPeriod: string }) => {
  if (trend === 'equal') return (
    <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
      <Minus className="w-3 h-3" /> invariato rispetto al {priorPeriod}
    </span>
  );
  if (trend === 'better') return (
    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
      <TrendingDown className="w-3 h-3" /> in miglioramento rispetto al {priorPeriod}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-red-400 font-medium">
      <TrendingUp className="w-3 h-3" /> in peggioramento rispetto al {priorPeriod}
    </span>
  );
};

const ScorePill = ({ score }: { score: number | null }) => {
  if (score === null) return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full border text-sm font-bold bg-gray-50 text-gray-300 border-gray-200">
      —
    </span>
  );
  const cls = score >= 120 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : score >= 100         ? 'bg-blue-50 text-blue-700 border-blue-200'
    :                        'bg-red-50 text-red-500 border-red-200';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-sm font-bold ${cls}`}>
      {score}
    </span>
  );
};

const numCls = 'w-20 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

type AreaKey = 'banking_and_investment_complaints' | 'financing_complaints';
type KpiKey  = 'avg_processing_time' | 'over_deadline_ratio';
type KpiField = 'value' | 'prior_value' | 'score';
type KpiTextField = 'name' | 'unit';
type KpiTierField = 'threshold' | 'target' | 'over_target';

/* ─── Layout risultati ───────────────────────────────────── */
interface ResultsLayoutProps {
  data: Driver01Data;
  isEditing: boolean;
  onUpdateKpi: (area: AreaKey, kpi: KpiKey, field: KpiField, raw: string) => void;
  onUpdateKpiText: (area: AreaKey, kpi: KpiKey, field: KpiTextField, value: string) => void;
  onUpdateKpiTier: (area: AreaKey, kpi: KpiKey, field: KpiTierField, raw: string) => void;
  onUpdateCombinedScore: (area: AreaKey, raw: string) => void;
  onUpdateOverallScore: (raw: string) => void;
  onUpdateAreaName: (area: AreaKey, value: string) => void;
  onUpdateHighlights: (value: string) => void;
}

const AREAS: { key: AreaKey }[] = [
  { key: 'banking_and_investment_complaints' },
  { key: 'financing_complaints' },
];

const KPIS: KpiKey[] = ['avg_processing_time', 'over_deadline_ratio'];

const KpiCard = ({
  kpi, areaKey, kpiKey, priorPeriod, isEditing, onUpdateKpi, onUpdateKpiText, onUpdateKpiTier,
}: {
  kpi: DriverKpi;
  areaKey: AreaKey;
  kpiKey: KpiKey;
  priorPeriod: string;
  isEditing: boolean;
  onUpdateKpi: ResultsLayoutProps['onUpdateKpi'];
  onUpdateKpiText: ResultsLayoutProps['onUpdateKpiText'];
  onUpdateKpiTier: ResultsLayoutProps['onUpdateKpiTier'];
}) => (
  <div className="rounded-lg bg-gray-50 px-4 py-3">
    <p className="text-[11px] text-gray-500 mb-2 leading-snug">
      {isEditing
        ? <input type="text" className={txtCls} value={kpi.name} onChange={e => onUpdateKpiText(areaKey, kpiKey, 'name', e.target.value)} />
        : kpi.name}
    </p>

    <div className="flex items-end justify-between mb-1.5">
      <div>
        {kpi.value !== null || isEditing ? (
          <>
            {isEditing
              ? <span className="inline-flex items-center gap-1"><input type="number" step="0.01" className={numCls} value={kpi.value ?? ''} onChange={e => onUpdateKpi(areaKey, kpiKey, 'value', e.target.value)} /><input type="text" className="w-16 bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50" value={kpi.unit} onChange={e => onUpdateKpiText(areaKey, kpiKey, 'unit', e.target.value)} /></span>
              : <><span className="text-xl font-bold text-[#1B365D]">{kpi.value}</span><span className="text-xs text-gray-400 ml-1">{kpi.unit}</span></>
            }
            {!isEditing && <span className="text-xs text-gray-400 ml-1">{kpi.unit}</span>}
          </>
        ) : (
          <span className="text-xl font-bold text-gray-200">— {kpi.unit}</span>
        )}
      </div>
      {isEditing
        ? <input type="number" className={numCls} value={kpi.score ?? ''} onChange={e => onUpdateKpi(areaKey, kpiKey, 'score', e.target.value)} />
        : <ScorePill score={kpi.score} />}
    </div>

    <div className="flex items-center justify-between min-h-[18px]">
      {kpi.prior_value !== null || isEditing ? (
        <>
          <span className="text-[11px] text-gray-400 flex items-center gap-1">
            nel {priorPeriod}:
            {isEditing
              ? <input type="number" step="0.01" className={numCls} value={kpi.prior_value ?? ''} onChange={e => onUpdateKpi(areaKey, kpiKey, 'prior_value', e.target.value)} />
              : <b className="text-gray-600">{kpi.prior_value} {kpi.unit}</b>}
          </span>
          {!isEditing && kpi.value !== null && kpi.prior_value !== null && (
            <TrendBadge trend={getTrend(kpi.value, kpi.prior_value)} priorPeriod={priorPeriod} />
          )}
        </>
      ) : (
        <span className="text-[11px] text-gray-300">nel {priorPeriod}: — {kpi.unit}</span>
      )}
    </div>

    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200 text-[10px] text-gray-400">
      <span className="inline-flex items-center gap-1">Soglia: {isEditing ? <input type="number" step="0.01" className={numCls} value={kpi.tier.threshold} onChange={e => onUpdateKpiTier(areaKey, kpiKey, 'threshold', e.target.value)} /> : <b>{kpi.tier.threshold}</b>}</span>
      <span className="inline-flex items-center gap-1">Target: {isEditing ? <input type="number" step="0.01" className={numCls} value={kpi.tier.target} onChange={e => onUpdateKpiTier(areaKey, kpiKey, 'target', e.target.value)} /> : <b>{kpi.tier.target}</b>}</span>
      <span className="inline-flex items-center gap-1">Over target: {isEditing ? <input type="number" step="0.01" className={numCls} value={kpi.tier.over_target} onChange={e => onUpdateKpiTier(areaKey, kpiKey, 'over_target', e.target.value)} /> : <b>{kpi.tier.over_target}</b>}</span>
    </div>
  </div>
);

const ResultsLayout = ({ data, isEditing, onUpdateKpi, onUpdateKpiText, onUpdateKpiTier, onUpdateCombinedScore, onUpdateOverallScore, onUpdateAreaName, onUpdateHighlights }: ResultsLayoutProps) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {AREAS.map(({ key }, ai) => {
        const area: ComplaintsArea = data[key];
        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: ai * 0.08 }}
            className="bg-white rounded-xl border border-gray-100 p-5"
          >
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-xs font-semibold text-[#1B365D] leading-snug flex-1">
                {isEditing
                  ? <input type="text" className={txtCls} value={area.area_name} onChange={e => onUpdateAreaName(key, e.target.value)} />
                  : area.area_name}
              </h3>
              <div className="shrink-0 flex flex-col items-end gap-0.5">
                <span className="text-[10px] text-gray-400">Punteggio</span>
                {isEditing
                  ? <input type="number" className={numCls} value={area.combined_score ?? ''} onChange={e => onUpdateCombinedScore(key, e.target.value)} />
                  : <ScorePill score={area.combined_score} />}
              </div>
            </div>

            <div className="space-y-4">
              {KPIS.map(kpiKey => (
                <KpiCard
                  key={kpiKey}
                  kpi={area[kpiKey]}
                  areaKey={key}
                  kpiKey={kpiKey}
                  priorPeriod={data.prior_period ?? '—'}
                  isEditing={isEditing}
                  onUpdateKpi={onUpdateKpi}
                  onUpdateKpiText={onUpdateKpiText}
                  onUpdateKpiTier={onUpdateKpiTier}
                />
              ))}
            </div>
          </motion.div>
        );
      })}
    </div>

    {/* Highlights */}
    {(isEditing || data.highlights.trim().length > 0) && (
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-3.5 h-3.5 text-[#1B365D]/60" />
          <p className="text-xs font-semibold text-[#1B365D]">Highlights</p>
        </div>
        {isEditing ? (
          <textarea
            className="w-full p-2 text-xs text-gray-600 leading-relaxed border border-[#1B365D]/15 rounded resize-y focus:outline-none focus:border-[#1B365D]/35 bg-gray-50/50 min-h-[160px]"
            value={data.highlights}
            onChange={e => onUpdateHighlights(e.target.value)}
          />
        ) : (
          <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{formatHighlights(data.highlights)}</p>
        )}
      </div>
    )}

  
  </div>
);

/* ─── Main ───────────────────────────────────────────────── */
const PerformanceReclamiContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver01Data>(reportId, 1);

  const [data,      setData]      = useState<Driver01Data | null>(null);
  const [savedData, setSavedData] = useState<Driver01Data | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      setSavedData(remoteData);
    }
  }, [remoteData]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  const onUpdateKpi = (areaKey: AreaKey, kpiKey: KpiKey, field: KpiField, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      [areaKey]: {
        ...prev[areaKey],
        [kpiKey]: { ...prev[areaKey][kpiKey], [field]: val },
      },
    } : prev));
  };

  const onUpdateKpiText = (areaKey: AreaKey, kpiKey: KpiKey, field: KpiTextField, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      [areaKey]: {
        ...prev[areaKey],
        [kpiKey]: { ...prev[areaKey][kpiKey], [field]: value },
      },
    } : prev));
  };

  const onUpdateKpiTier = (areaKey: AreaKey, kpiKey: KpiKey, field: KpiTierField, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      [areaKey]: {
        ...prev[areaKey],
        [kpiKey]: {
          ...prev[areaKey][kpiKey],
          tier: { ...prev[areaKey][kpiKey].tier, [field]: val },
        },
      },
    } : prev));
  };

  const onUpdateAreaName = (areaKey: AreaKey, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      [areaKey]: { ...prev[areaKey], area_name: value },
    } : prev));
  };

  const onUpdateCombinedScore = (areaKey: AreaKey, raw: string) => {
    const val = parseInt(raw, 10);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      [areaKey]: { ...prev[areaKey], combined_score: val },
    } : prev));
  };

  const onUpdateOverallScore = (raw: string) => {
    const val = parseInt(raw, 10);
    if (isNaN(val)) return;
    setData(prev => (prev ? { ...prev, overall_score: val } : prev));
  };

  const onUpdateHighlights = (value: string) => {
    setData(prev => (prev ? { ...prev, highlights: value } : prev));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1B365D]/5 border border-[#1B365D]/10">
          <span className="text-[10px] uppercase tracking-wide text-[#1B365D]/60 font-semibold">Periodo</span>
          <span className="text-xs font-bold text-[#1B365D]">{data.period}</span>
          {data.prior_period && (
            <>
              <span className="text-[10px] text-gray-400">·</span>
              <span className="text-[10px] text-gray-500">confronto {data.prior_period}</span>
            </>
          )}
        </div>
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      <ResultsLayout
        data={data}
        isEditing={isEditing}
        onUpdateKpi={onUpdateKpi}
        onUpdateKpiText={onUpdateKpiText}
        onUpdateKpiTier={onUpdateKpiTier}
        onUpdateCombinedScore={onUpdateCombinedScore}
        onUpdateOverallScore={onUpdateOverallScore}
        onUpdateAreaName={onUpdateAreaName}
        onUpdateHighlights={onUpdateHighlights}
      />
    </div>
  );
};

export default PerformanceReclamiContainer;
